/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * CR402 Phase 2: Component Editor Panel
 * CR308: Added StarburstAssetPicker to Starburst editor
 * CR309: Added WatermarkEditor for watermark configuration
 * CR310: Added RibbonAssetPicker for ribbon graphic selection
 * 
 * Displays controls for the selected component.
 * Groups controls into: Typography, Color, Layout, Visibility, Advanced.
 */

import React, { useMemo, useCallback } from "react";
import { cn } from "@/lib/utils";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { 
  Type, 
  Palette, 
  Layout, 
  Eye,
  Settings,
  Info,
  CheckCircle2,
  XCircle,
  Star,
  Droplets,
  Flag,
  Image,
  HelpCircle,
} from "lucide-react";

import { RibbonDebugSampleCard } from "./ribbon/RibbonDebugSampleCard";

import type { ComponentsConfig, ComponentTypography, ComponentConfig } from "./DealCardsTab";
import type { CardStyling } from "./CardStylingPanel";
import { 
  RibbonComposer, 
  DEFAULT_RIBBON_COMPOSER_CONFIG,
  type RibbonComposerConfig,
} from "@/components/admin/deal-cards/RibbonComposer";
import { 
  RibbonStylingSection,
  type RibbonStylingConfig,
} from "@/components/admin/deal-cards/RibbonStylingSection";
import { RibbonColorSection } from "@/components/admin/deal-cards/RibbonColorSection";
import type { RibbonColorConfig, RibbonColorSource } from "@/lib/deal-cards/resolveRibbonColor";
import { getResolvedRibbonHex } from "@/lib/deal-cards/resolveRibbonColor";
import type { DealTypeThemeColors } from "@/lib/deals/globalThemeTokens";
import { StarburstAssetPicker } from "./StarburstAssetPicker";
import { WatermarkEditor } from "./WatermarkEditor";
import { RibbonAssetPicker, type RibbonGraphicSource } from "./RibbonAssetPicker";
import { useAssetsByType } from "@/hooks/useAssetsByType";

// CR344 G5: Content editors
import {
  HeadlineEditor,
  DatesEditor,
  PromoBadgesEditor,
  DetailsPillsEditor,
  ValueBoxEditor,
  SpendBoxEditor,
  RewardBoxEditor,
  CTAButtonEditor,
  ActionsRowEditor,
  FeedbackEditor,
} from "./ComponentContentEditors";

// CR344 G3: Labels editor
import { LabelsEditor } from "./LabelsEditor";

// CR481: Card Snippet config
import {
  DEFAULT_CARD_SNIPPET_CONFIG,
  type CardSnippetConfig,
  type BboxQualityLevel,
  type FallbackMode,
  type CornerRadius,
  type BorderStyle,
  type AspectRatio,
} from "@/lib/deal-cards/cardSnippetConfig";

// ================================================================
// TYPES
// ================================================================

interface ComponentEditorPanelProps {
  componentKey: string;
  componentLabel: string;
  componentDescription?: string;
  components: ComponentsConfig;
  styling: CardStyling;
  onChange: (update: Partial<ComponentsConfig>) => void;
  onStylingChange: (update: Partial<CardStyling>) => void;
  structureEnabled: boolean;
  sampleDeal?: Record<string, unknown> | null;
  dealTypeKey?: string;
  /** CR344 G3: Labels for labels editor */
  labels?: Record<string, string>;
  onLabelsChange?: (update: Record<string, string>) => void;
  /** CR305: Theme Labs per-deal-type themes for ribbon color SSOT */
  dealTypeThemes?: Record<string, DealTypeThemeColors> | null;
}

// Font family options
const FONT_FAMILIES = [
  { value: "inherit", label: "Default (inherit)" },
  { value: "Inter", label: "Inter" },
  { value: "DM Sans", label: "DM Sans" },
  { value: "Space Grotesk", label: "Space Grotesk" },
  { value: "Manrope", label: "Manrope" },
  { value: "Montserrat", label: "Montserrat" },
  { value: "Source Sans 3", label: "Source Sans 3" },
];

// Font weight options
const FONT_WEIGHTS = [
  { value: 400, label: "Regular (400)" },
  { value: 500, label: "Medium (500)" },
  { value: 600, label: "Semibold (600)" },
  { value: 700, label: "Bold (700)" },
];

// ================================================================
// HELPERS
// ================================================================

function VisibilityChip({ enabled }: { enabled: boolean }) {
  return (
    <Badge 
      variant={enabled ? "default" : "secondary"} 
      className={cn(
        "text-[10px] px-1.5 py-0 h-5",
        !enabled && "bg-muted text-muted-foreground"
      )}
    >
      {enabled ? (
        <>
          <CheckCircle2 className="h-3 w-3 mr-0.5" />
          Enabled
        </>
      ) : (
        <>
          <XCircle className="h-3 w-3 mr-0.5" />
          Disabled
        </>
      )}
    </Badge>
  );
}

function TypographyControls({
  typography,
  onChange,
  label,
}: {
  typography?: ComponentTypography;
  onChange: (update: ComponentTypography) => void;
  label: string;
}) {
  const fontFamily = typography?.fontFamily ?? "inherit";
  const fontSize = typography?.fontSize ?? 14;
  const fontWeight = typography?.fontWeight ?? 500;

  return (
    <div className="space-y-3">
      <Label className="text-xs font-medium text-muted-foreground">{label}</Label>
      <div className="grid grid-cols-3 gap-3">
        <div className="space-y-1">
          <Label className="text-[10px]">Font</Label>
          <Select 
            value={fontFamily} 
            onValueChange={(v) => onChange({ ...typography, fontFamily: v })}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FONT_FAMILIES.map((f) => (
                <SelectItem key={f.value} value={f.value} className="text-xs">
                  {f.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-[10px]">Size (px)</Label>
          <Input
            type="number"
            value={fontSize}
            onChange={(e) => onChange({ ...typography, fontSize: parseInt(e.target.value) || 14 })}
            className="h-8 text-xs"
            min={8}
            max={48}
          />
        </div>
        <div className="space-y-1">
          <Label className="text-[10px]">Weight</Label>
          <Select 
            value={String(fontWeight)} 
            onValueChange={(v) => onChange({ ...typography, fontWeight: parseInt(v) })}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FONT_WEIGHTS.map((w) => (
                <SelectItem key={w.value} value={String(w.value)} className="text-xs">
                  {w.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}

function SubElementConfig({
  config,
  onChange,
  label,
  showTypography = true,
}: {
  config?: ComponentConfig;
  onChange: (update: ComponentConfig) => void;
  label: string;
  showTypography?: boolean;
}) {
  const enabled = config?.enabled ?? true;

  return (
    <div className="space-y-2 p-3 bg-muted/40 rounded-md">
      <div className="flex items-center justify-between">
        <Label className="text-xs font-medium">{label}</Label>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-muted-foreground">Display</span>
          <Switch
            checked={enabled}
            onCheckedChange={(v) => onChange({ ...config, enabled: v })}
          />
        </div>
      </div>
      {showTypography && enabled && (
        <TypographyControls
          typography={config?.typography}
          onChange={(typo) => onChange({ ...config, typography: typo })}
          label="Typography"
        />
      )}
    </div>
  );
}

// ================================================================
// COMPONENT-SPECIFIC EDITORS
// ================================================================

function HeaderEditor({
  components,
  onChange,
}: {
  components: ComponentsConfig;
  onChange: (update: Partial<ComponentsConfig>) => void;
}) {
  return (
    <div className="space-y-4">
      <TypographyControls
        typography={components.header_front?.typography}
        onChange={(typo) => onChange({ 
          header_front: { ...components.header_front, typography: typo } 
        })}
        label="Header Typography"
      />
    </div>
  );
}

// ================================================================
// CR450: HEADER (BACK) EDITOR
// ================================================================

type HeaderBackMode = "same_as_front" | "merchant_name" | "reward_amount" | "custom";

const HEADER_BACK_MODES: { value: HeaderBackMode; label: string; description: string }[] = [
  { value: "same_as_front", label: "Display same as front", description: "Mirrors the front header content" },
  { value: "merchant_name", label: "Use merchant name", description: "Shows the merchant display name as title" },
  { value: "reward_amount", label: "Reward amount", description: "Shows the reward value as title" },
  { value: "custom", label: "Custom (template)", description: "Use a custom template with tags" },
];

const TEMPLATE_TAGS = [
  { tag: "{{title}}", label: "Title" },
  { tag: "{{merchant}}", label: "Merchant" },
  { tag: "{{reward}}", label: "Reward" },
  { tag: "{{condition}}", label: "Condition" },
  { tag: "{{deal_type}}", label: "Deal Type" },
];

function HeaderBackEditor({
  components,
  onChange,
}: {
  components: ComponentsConfig;
  onChange: (update: Partial<ComponentsConfig>) => void;
}) {
  const backConfig = components.header_back as Record<string, any> | undefined;
  const mode: HeaderBackMode = backConfig?.mode ?? "custom";
  const customTemplate: string = backConfig?.custom_template ?? "{{title}}\n{{condition}}";
  const inheritFrontStyle: boolean = backConfig?.inherit_front_style ?? true;
  const bgColorSource: string = backConfig?.background?.color_source ?? "system";
  const bgColorHex: string = backConfig?.background?.color_hex ?? "#334155";
  const typography = backConfig?.typography;
  const fontStyle: string = typography?.fontStyle ?? "normal";
  const textColorHex: string = typography?.color_hex ?? "#ffffff";

  const updateBack = (patch: Record<string, any>) => {
    onChange({
      header_back: { ...backConfig, ...patch },
    });
  };

  return (
    <div className="space-y-5">
      {/* A) Back Header Content — Mode Selector */}
      <div className="space-y-3">
        <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Back Header Content</Label>
        <Select value={mode} onValueChange={(v) => updateBack({ mode: v })}>
          <SelectTrigger className="h-9">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {HEADER_BACK_MODES.map((m) => (
              <SelectItem key={m.value} value={m.value} className="text-sm">
                <div>
                  <span>{m.label}</span>
                  <span className="text-xs text-muted-foreground ml-2">— {m.description}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Same as front: inherit style toggle */}
      {mode === "same_as_front" && (
        <div className="flex items-center justify-between p-3 bg-muted/40 rounded-md">
          <div>
            <Label className="text-xs font-medium">Inherit front styling</Label>
            <p className="text-[10px] text-muted-foreground">Use front header typography and background</p>
          </div>
          <Switch
            checked={inheritFrontStyle}
            onCheckedChange={(v) => updateBack({ inherit_front_style: v })}
          />
        </div>
      )}

      {/* Custom: template editor */}
      {mode === "custom" && (
        <div className="space-y-3">
          <Label className="text-xs font-medium">Custom Template</Label>
          <p className="text-[10px] text-muted-foreground">
            First line = title, remaining lines = subtitle. Use tags to insert deal data.
          </p>
          <textarea
            value={customTemplate}
            onChange={(e) => updateBack({ custom_template: e.target.value })}
            className="w-full h-20 text-sm font-mono rounded-md border bg-background p-2 resize-none"
            placeholder="{{title}}\n{{condition}}"
          />
          <div className="flex flex-wrap gap-1.5">
            {TEMPLATE_TAGS.map((t) => (
              <button
                key={t.tag}
                type="button"
                className="text-[10px] px-2 py-0.5 rounded-full border bg-muted/50 hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                onClick={() => {
                  updateBack({ custom_template: (customTemplate || "") + t.tag });
                }}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <Separator />

      {/* B) Header Strip Styling */}
      <div className="space-y-3">
        <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Header Strip Styling</Label>
        
        {/* Background */}
        <div className="space-y-2 p-3 bg-muted/40 rounded-md">
          <Label className="text-xs font-medium">Background</Label>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-[10px]">Source</Label>
              <Select 
                value={bgColorSource} 
                onValueChange={(v) => updateBack({ background: { ...backConfig?.background, color_source: v } })}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="system" className="text-xs">System default</SelectItem>
                  <SelectItem value="custom_hex" className="text-xs">Custom hex</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {bgColorSource === "custom_hex" && (
              <div className="space-y-1">
                <Label className="text-[10px]">Color</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={bgColorHex}
                    onChange={(e) => updateBack({ background: { ...backConfig?.background, color_source: "custom_hex", color_hex: e.target.value } })}
                    className="w-8 h-8 rounded border cursor-pointer"
                  />
                  <Input
                    value={bgColorHex}
                    onChange={(e) => updateBack({ background: { ...backConfig?.background, color_source: "custom_hex", color_hex: e.target.value } })}
                    className="h-8 text-xs font-mono w-24"
                    placeholder="#334155"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Typography */}
        {!(mode === "same_as_front" && inheritFrontStyle) && (
          <>
            <TypographyControls
              typography={typography}
              onChange={(typo) => updateBack({ typography: { ...typography, ...typo } })}
              label="Typography"
            />
            <div className="grid grid-cols-2 gap-3 p-3 bg-muted/40 rounded-md">
              <div className="space-y-1">
                <Label className="text-[10px]">Font Style</Label>
                <Select 
                  value={fontStyle} 
                  onValueChange={(v) => updateBack({ typography: { ...typography, fontStyle: v } })}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="normal" className="text-xs">Normal</SelectItem>
                    <SelectItem value="italic" className="text-xs">Italic</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-[10px]">Text Color</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={textColorHex}
                    onChange={(e) => updateBack({ typography: { ...typography, color_hex: e.target.value } })}
                    className="w-8 h-8 rounded border cursor-pointer"
                  />
                  <Input
                    value={textColorHex}
                    onChange={(e) => updateBack({ typography: { ...typography, color_hex: e.target.value } })}
                    className="h-8 text-xs font-mono w-24"
                    placeholder="#ffffff"
                  />
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function RibbonEditor({
  components,
  styling,
  onChange,
  onStylingChange,
  structureEnabled,
  sampleDeal,
  dealTypeKey,
  dealTypeThemes,
}: {
  components: ComponentsConfig;
  styling: CardStyling;
  onChange: (update: Partial<ComponentsConfig>) => void;
  onStylingChange: (update: Partial<CardStyling>) => void;
  structureEnabled: boolean;
  sampleDeal?: Record<string, unknown> | null;
  dealTypeKey?: string;
  dealTypeThemes?: Record<string, DealTypeThemeColors> | null;
}) {
  // Convert to RibbonComposerConfig
  const ribbonComposerConfig = useMemo(() => {
    const ribbonConfig = components.ribbon;
    if (!ribbonConfig) {
      return { ...DEFAULT_RIBBON_COMPOSER_CONFIG };
    }
    
    const composerRibbon = ribbonConfig as unknown as RibbonComposerConfig;
    if (composerRibbon.primary && composerRibbon.typography) {
      return composerRibbon;
    }
    
    return {
      ...DEFAULT_RIBBON_COMPOSER_CONFIG,
      position: (ribbonConfig as any).position,
      offsetX: (ribbonConfig as any).offsetX,
      offsetY: (ribbonConfig as any).offsetY,
    };
  }, [components.ribbon]);
  
  // CR310: Get current graphic source and asset ID
  const graphicSource: RibbonGraphicSource = (components.ribbon as any)?.graphic_source || "system";
  const ribbonAssetId = (components.ribbon as any)?.asset_id || null;
  
  // CR337: Get color config from components.ribbon
  const colorConfig: RibbonColorConfig = useMemo(() => ({
    color_source: ((components.ribbon as any)?.color_source as RibbonColorSource) ?? "theme_token",
    color_hex: (components.ribbon as any)?.color_hex ?? null,
  }), [components.ribbon]);
  
  // CR337: Load ribbon assets to get default_tint
  const { assets: ribbonAssets } = useAssetsByType({ type: "ribbon" });
  const selectedRibbonAsset = useMemo(
    () => ribbonAssets.find(a => a.id === ribbonAssetId) ?? null,
    [ribbonAssets, ribbonAssetId]
  );
  const assetDefaultTint = selectedRibbonAsset?.default_tint ?? null;
  
  const handleRibbonComposerChange = useCallback((config: RibbonComposerConfig) => {
    onChange({
      ribbon: {
        ...config,
        // Preserve graphic settings
        graphic_source: graphicSource,
        asset_id: ribbonAssetId,
        // CR337: Preserve color settings
        color_source: colorConfig.color_source,
        color_hex: colorConfig.color_hex,
      } as unknown as ComponentsConfig["ribbon"],
    });
  }, [onChange, graphicSource, ribbonAssetId, colorConfig]);
  
  // CR310: Handle graphic source/asset change
  const handleGraphicChange = useCallback((source: RibbonGraphicSource, assetId: string | null) => {
    onChange({
      ribbon: {
        ...components.ribbon,
        graphic_source: source,
        asset_id: assetId,
      } as unknown as ComponentsConfig["ribbon"],
    });
  }, [onChange, components.ribbon]);
  
  // CR337: Handle color config change
  const handleColorChange = useCallback((config: RibbonColorConfig) => {
    onChange({
      ribbon: {
        ...components.ribbon,
        color_source: config.color_source,
        color_hex: config.color_hex,
      } as unknown as ComponentsConfig["ribbon"],
    });
  }, [onChange, components.ribbon]);
  
  // Ribbon styling
  const ribbonStyling: RibbonStylingConfig = {
    ribbonBaseHex: styling.ribbonBaseHex,
    ribbonBorderHex: styling.ribbonBorderHex,
    gradientFrom: styling.ribbonGradientFrom,
    gradientTo: styling.ribbonGradientTo,
    borderColor: styling.ribbonBorderColor,
  };
  
  const hasRibbonBaseOverride = !!styling.ribbonBaseHex;
  const hasRibbonBorderOverride = !!styling.ribbonBorderHex;

  return (
    <div className="space-y-4">
      {/* CR310: Ribbon Graphic Section */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Flag className="h-4 w-4 text-muted-foreground" />
          <Label className="text-xs font-medium text-muted-foreground">Ribbon Graphic</Label>
        </div>
        <RibbonAssetPicker
          graphicSource={graphicSource}
          selectedAssetId={ribbonAssetId}
          ribbonEnabled={structureEnabled}
          onChange={handleGraphicChange}
        />
      </div>
      
      <Separator />
      
      {/* CR337 + CR305 + CR306: Ribbon Color Section — uses Theme Labs SSOT + styling fallback */}
      <RibbonColorSection
        colorConfig={colorConfig}
        dealTypeKey={dealTypeKey || "BONUS"}
        assetDefaultTint={assetDefaultTint}
        hasAsset={graphicSource === "asset" && !!ribbonAssetId}
        onChange={handleColorChange}
        dealTypeThemes={dealTypeThemes}
        stylingFallbackHex={styling.ribbonBaseHex ?? null}
      />
      
      <Separator />
      
      <RibbonComposer
        config={ribbonComposerConfig}
        onChange={handleRibbonComposerChange}
        structureEnabled={structureEnabled}
        sampleDeal={sampleDeal}
        dealTypeKey={dealTypeKey || "BONUS"}
        ribbonColorHex={getResolvedRibbonHex(colorConfig, (dealTypeKey || "BONUS") as any, assetDefaultTint, dealTypeThemes, styling.ribbonBaseHex ?? null)}
      />
      
      <Separator />
      
      <RibbonStylingSection
        styling={ribbonStyling}
        onChange={(update) => {
          onStylingChange({
            ribbonBaseHex: update.ribbonBaseHex,
            ribbonBorderHex: update.ribbonBorderHex,
            ribbonGradientFrom: update.gradientFrom,
            ribbonGradientTo: update.gradientTo,
            ribbonBorderColor: update.borderColor,
          });
        }}
        onClearBase={() => {
          onStylingChange({
            ribbonBaseHex: null,
            ribbonBorderHex: null,
            ribbonGradientFrom: undefined,
            ribbonGradientTo: undefined,
            ribbonBorderColor: undefined,
          });
        }}
        onClearBorder={() => {
          onStylingChange({
            ribbonBorderHex: null,
          });
        }}
        hasBaseOverride={hasRibbonBaseOverride}
        hasBorderOverride={hasRibbonBorderOverride}
        dealTypeKey={dealTypeKey || "BONUS"}
      />
      
      <Separator />
      
      {/* CR338: Debug Ribbon Sample Card */}
      <RibbonDebugSampleCard
        dealTypeKey={dealTypeKey || "BONUS"}
        ribbonComponentConfig={components.ribbon as any}
        colorConfig={colorConfig}
        graphicSource={graphicSource}
        assetId={ribbonAssetId}
        assetDefaultTint={assetDefaultTint}
        assetName={selectedRibbonAsset?.name ?? null}
        sampleDeal={sampleDeal}
      />
    </div>
  );
}

function StarburstEditor({
  components,
  onChange,
  structureEnabled,
}: {
  components: ComponentsConfig;
  onChange: (update: Partial<ComponentsConfig>) => void;
  structureEnabled: boolean;
}) {
  // CR308: Get current asset ID from starburst config
  const currentAssetId = components.starburst?.assetId ?? null;

  const handleAssetSelect = (assetId: string | null) => {
    onChange({
      starburst: { ...components.starburst, assetId: assetId ?? undefined }
    });
  };

  return (
    <div className="space-y-4">
      {/* CR308: Starburst Graphic Section */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Star className="h-4 w-4 text-muted-foreground" />
          <Label className="text-xs font-medium text-muted-foreground">Starburst Graphic</Label>
        </div>
        <StarburstAssetPicker
          selectedAssetId={currentAssetId}
          starburstEnabled={structureEnabled}
          onSelectAsset={handleAssetSelect}
        />
      </div>

      <Separator />

      {/* Typography Section */}
      <SubElementConfig
        config={components.starburst?.value}
        onChange={(cfg) => onChange({
          starburst: { ...components.starburst, value: cfg }
        })}
        label="Value (styling)"
        showTypography={true}
      />

      <SubElementConfig
        config={components.starburst?.name}
        onChange={(cfg) => onChange({
          starburst: { ...components.starburst, name: cfg }
        })}
        label="Name Label"
        showTypography={true}
      />
    </div>
  );
}

function GenericEditor({
  componentKey,
  componentLabel,
}: {
  componentKey: string;
  componentLabel: string;
}) {
  return (
    <div className="p-4 bg-muted/20 rounded-lg text-center">
      <Info className="h-6 w-6 text-muted-foreground mx-auto mb-2" />
      <p className="text-sm text-muted-foreground">
        <strong>{componentLabel}</strong> configuration is managed through Structure toggles.
      </p>
      <p className="text-xs text-muted-foreground mt-1">
        Typography and advanced styling coming soon.
      </p>
    </div>
  );
}

// ================================================================
// CR481: CARD SNIPPET EDITOR
// ================================================================

const QUALITY_OPTIONS: { value: BboxQualityLevel; label: string }[] = [
  { value: "tight", label: "Tight (≤15% area)" },
  { value: "medium", label: "Medium (≤30% area)" },
  { value: "loose", label: "Loose (≤55% area)" },
];

const FALLBACK_OPTIONS: { value: FallbackMode; label: string }[] = [
  { value: "none", label: "None (hide)" },
  { value: "brand_artwork", label: "Brand artwork" },
  { value: "issuer_artwork", label: "Issuer artwork" },
  { value: "generic_placeholder", label: "Generic placeholder" },
];

const CORNER_OPTIONS: { value: CornerRadius; label: string }[] = [
  { value: "none", label: "None" },
  { value: "sm", label: "Small" },
  { value: "md", label: "Medium" },
  { value: "lg", label: "Large" },
];

const BORDER_OPTIONS: { value: BorderStyle; label: string }[] = [
  { value: "subtle", label: "Subtle" },
  { value: "strong", label: "Strong" },
];

const ASPECT_OPTIONS: { value: AspectRatio; label: string }[] = [
  { value: "3:2", label: "3:2" },
  { value: "4:3", label: "4:3" },
  { value: "1:1", label: "1:1 (Square)" },
];

function CardSnippetEditor({
  components,
  onChange,
}: {
  components: ComponentsConfig;
  onChange: (update: Partial<ComponentsConfig>) => void;
}) {
  const raw = (components as any)?.card_snippet ?? {};
  const config: CardSnippetConfig = { ...DEFAULT_CARD_SNIPPET_CONFIG, ...raw };

  const update = (patch: Partial<CardSnippetConfig>) => {
    onChange({ card_snippet: { ...config, ...patch } } as any);
  };

  return (
    <div className="space-y-4">
      <Accordion type="multiple" defaultValue={["eligibility", "fallback", "presentation"]} className="space-y-2">
        {/* Eligibility */}
        <AccordionItem value="eligibility" className="border rounded-lg px-3">
          <AccordionTrigger className="text-xs font-semibold py-2">
            <div className="flex items-center gap-2">
              <Eye className="h-3.5 w-3.5" />
              Eligibility
            </div>
          </AccordionTrigger>
          <AccordionContent className="space-y-3 pb-3">
            <div className="flex items-center justify-between">
              <Label className="text-xs">Require valid snippet</Label>
              <Switch checked={config.require_valid_snippet} onCheckedChange={(v) => update({ require_valid_snippet: v })} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Min bbox quality</Label>
              <Select value={config.min_bbox_quality} onValueChange={(v) => update({ min_bbox_quality: v as BboxQualityLevel })}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {QUALITY_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value} className="text-xs">{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-xs">Allow loose if only option</Label>
              <Switch checked={config.allow_loose_if_only_option} onCheckedChange={(v) => update({ allow_loose_if_only_option: v })} />
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-xs">Hide if bad bbox</Label>
              <Switch checked={config.hide_if_bad_bbox} onCheckedChange={(v) => update({ hide_if_bad_bbox: v })} />
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-xs">Show warning (admin only)</Label>
              <Switch checked={config.show_warning_bad_bbox} onCheckedChange={(v) => update({ show_warning_bad_bbox: v })} />
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Fallback */}
        <AccordionItem value="fallback" className="border rounded-lg px-3">
          <AccordionTrigger className="text-xs font-semibold py-2">
            <div className="flex items-center gap-2">
              <Image className="h-3.5 w-3.5" />
              Fallback
            </div>
          </AccordionTrigger>
          <AccordionContent className="space-y-3 pb-3">
            <div className="space-y-1">
              <Label className="text-xs">Fallback mode</Label>
              <Select value={config.fallback_mode} onValueChange={(v) => update({ fallback_mode: v as FallbackMode })}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FALLBACK_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value} className="text-xs">{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Presentation */}
        <AccordionItem value="presentation" className="border rounded-lg px-3">
          <AccordionTrigger className="text-xs font-semibold py-2">
            <div className="flex items-center gap-2">
              <Settings className="h-3.5 w-3.5" />
              Presentation
            </div>
          </AccordionTrigger>
          <AccordionContent className="space-y-3 pb-3">
            <div className="space-y-1">
              <Label className="text-xs">Corner radius</Label>
              <Select value={config.corner_radius} onValueChange={(v) => update({ corner_radius: v as CornerRadius })}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CORNER_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value} className="text-xs">{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-xs">Show border</Label>
              <Switch checked={config.show_border} onCheckedChange={(v) => update({ show_border: v })} />
            </div>
            {config.show_border && (
              <div className="space-y-1">
                <Label className="text-xs">Border style</Label>
                <Select value={config.border_style} onValueChange={(v) => update({ border_style: v as BorderStyle })}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {BORDER_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value} className="text-xs">{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1">
              <Label className="text-xs">Aspect ratio</Label>
              <Select value={config.aspect_ratio} onValueChange={(v) => update({ aspect_ratio: v as AspectRatio })}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ASPECT_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value} className="text-xs">{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <Label className="text-xs">Show "From flyer" badge</Label>
              <Switch checked={config.show_snippet_badge} onCheckedChange={(v) => update({ show_snippet_badge: v })} />
            </div>
            {config.show_snippet_badge && (
              <div className="space-y-1">
                <Label className="text-xs">Badge text</Label>
                <Input
                  value={config.snippet_badge_text ?? "From flyer"}
                  onChange={(e) => update({ snippet_badge_text: e.target.value || null })}
                  className="h-8 text-xs"
                  placeholder="From flyer"
                />
              </div>
            )}
          </AccordionContent>
        </AccordionItem>
        {/* CR486: Scoring explanation */}
        <AccordionItem value="scoring_info" className="border rounded-lg px-3">
          <AccordionTrigger className="text-xs font-semibold py-2">
            <div className="flex items-center gap-2">
              <HelpCircle className="h-3.5 w-3.5" />
              How snippet selection works
            </div>
          </AccordionTrigger>
          <AccordionContent className="pb-3">
            <div className="text-xs text-muted-foreground space-y-2 leading-relaxed">
              <p>
                When multiple snippet candidates exist on a page, the system picks the one with the highest <strong>locator score</strong>:
              </p>
              <pre className="bg-muted/50 rounded p-2 text-[11px] font-mono">
{`score = key_phrase_hits
      − area_penalty
      − overlap_penalty`}
              </pre>
              <ul className="list-disc pl-4 space-y-1">
                <li><strong>Key phrase hits</strong> — how many important words (brand, amount, reward text) are inside the box.</li>
                <li><strong>Area penalty</strong> — penalty if the box is too small (&lt;4%) or too large (&gt;18% of page).</li>
                <li><strong>Overlap penalty</strong> — penalty when multiple boxes overlap heavily (ambiguity).</li>
              </ul>
              <p>
                The final score is stored on the snippet artifact and shown in admin diagnostics and the Snippet Artifacts viewer.
              </p>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}

// ================================================================
// MAIN COMPONENT
// ================================================================

export function ComponentEditorPanel({
  componentKey,
  componentLabel,
  componentDescription,
  components,
  styling,
  onChange,
  onStylingChange,
  structureEnabled,
  sampleDeal,
  dealTypeKey,
  labels,
  onLabelsChange,
  dealTypeThemes,
}: ComponentEditorPanelProps) {
  // Render component-specific editor
  const renderEditor = () => {
    switch (componentKey) {
      case "header":
        return <HeaderEditor components={components} onChange={onChange} />;
      
      case "header_back":
        return <HeaderBackEditor components={components} onChange={onChange} />;
      
      case "ribbon":
        return (
          <RibbonEditor
            components={components}
            styling={styling}
            onChange={onChange}
            onStylingChange={onStylingChange}
            structureEnabled={structureEnabled}
            sampleDeal={sampleDeal}
            dealTypeKey={dealTypeKey}
            dealTypeThemes={dealTypeThemes}
          />
        );
      
      case "starburst":
        return <StarburstEditor components={components} onChange={onChange} structureEnabled={structureEnabled} />;
      
      case "watermark":
        return <WatermarkEditor components={components} onChange={onChange} />;
      
      // CR344 G3: Labels editor
      case "labels":
        return (
          <LabelsEditor
            labels={labels ?? {}}
            onChange={onLabelsChange ?? (() => {})}
          />
        );
      
      // CR344 G5: Real component editors
      case "headline":
        return <HeadlineEditor components={components} onChange={onChange} />;
      case "dates":
        return <DatesEditor components={components} onChange={onChange} />;
      case "promo_badges":
        return <PromoBadgesEditor components={components} onChange={onChange} />;
      case "details_pills":
        return <DetailsPillsEditor components={components} onChange={onChange} />;
      case "value_box":
        return <ValueBoxEditor components={components} onChange={onChange} labels={labels} onLabelsChange={onLabelsChange} />;
      case "spend_box":
        return <SpendBoxEditor components={components} onChange={onChange} labels={labels} onLabelsChange={onLabelsChange} />;
      case "reward_box":
        return <RewardBoxEditor components={components} onChange={onChange} labels={labels} onLabelsChange={onLabelsChange} />;
      case "cta_button":
        return <CTAButtonEditor components={components} onChange={onChange} />;
      case "actions":
        return <ActionsRowEditor components={components} onChange={onChange} />;
      case "feedback":
        return <FeedbackEditor components={components} onChange={onChange} />;
      case "card_snippet":
        return <CardSnippetEditor components={components} onChange={onChange} />;
      
      default:
        return <GenericEditor componentKey={componentKey} componentLabel={componentLabel} />;
    }
  };

  // CR344: Labels don't have a structure toggle
  const isLabels = componentKey === "labels";

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold">{componentLabel}</h3>
          {componentDescription && (
            <p className="text-xs text-muted-foreground">{componentDescription}</p>
          )}
        </div>
        {!isLabels && <VisibilityChip enabled={structureEnabled} />}
      </div>
      
      <Separator />
      
      {/* Editor */}
      {!isLabels && !structureEnabled ? (
        <div className="p-4 bg-muted/20 rounded-lg text-center">
          <XCircle className="h-6 w-6 text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">
            This component is disabled in Structure.
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Enable it in the Template Editor → Structure tab to configure.
          </p>
        </div>
      ) : (
        renderEditor()
      )}
    </div>
  );
}

export default ComponentEditorPanel;
